import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Presentation,
  Video,
  FileSpreadsheet,
  BookOpen,
  ShieldCheck,
  FileCode2,
  AlertTriangle,
  RefreshCcw,
  BarChart3,
  Layers,
  Target,
} from "lucide-react";
import MascotOrb from "./MascotOrb";
import traineeAvatar from "@/assets/avatar.png";

gsap.registerPlugin(ScrollTrigger);

// Step meta (5 steps)
const STEPS = [
  { n: "01", key: "content", label: "Content" },
  { n: "02", key: "engine", label: "AI Engine" },
  { n: "03", key: "sessions", label: "Sessions" },
  { n: "04", key: "tutor", label: "AI Tutor" },
  { n: "05", key: "analytics", label: "Analytics" },
];

const STAGE_COPY = [
  {
    eyebrow: "01 · Content",
    title: "Utilize your current knowledge base",
    body: "AI Trainer ingests PDFs, PPTs, videos, docs, SOPs, and spreadsheets — the training content your team already uses.",
  },
  {
    eyebrow: "02 · AI Engine",
    title: "Sparkl AI maps and categorizes the data",
    body: "The system ingests content, understands it, and turns it into a structured learning model — not just searchable documents.",
  },
  {
    eyebrow: "03 · Sessions",
    title: "Structured AI sessions are created",
    body: "The engine outputs ready-to-run learning sessions, one clean module at a time.",
  },
  {
    eyebrow: "04 · AI Tutor",
    title: "AI tutor guides the learner",
    body: "A non-human AI tutor delivers each session as clear learning slides — explaining, pacing, and reinforcing.",
  },
  {
    eyebrow: "05 · Analytics",
    title: "Trainers get insight and can intervene",
    body: "Spot weak areas and at-risk learners, then re-certify them in a click.",
  },
];

const STAGE_BOUNDS = [0.10, 0.30, 0.37, 0.55, 1.01];
function progressToStage(p) {
  for (let i = 0; i < STAGE_BOUNDS.length; i++) {
    if (p < STAGE_BOUNDS[i]) return i;
  }
  return STAGE_BOUNDS.length - 1;
}

// Sessions in a horizontal line (no overlaps)
const SESSIONS = [
  {
    title: "New Hire Onboarding",
    image:
      "https://asset.sparkl.me/pb/poc-folder/sniIziQDdttDV3dab4jGIWqH5BuwSRmevTtWLFrb.jpg",
    color: "blue",
    x: -450,
    y: 0,
    minutes: 12,
    steps: 6,
  },
  {
    title: "Soft Skills",
    image:
      "https://asset.sparkl.me/pb/poc-folder/EzNQorLTq0YoqK0Viz1Z1g6Hraz4uBdvVvvxxL7v.jpg",
    color: "violet",
    x: -150,
    y: 0,
    focus: true,
    minutes: 15,
    steps: 8,
  },
  {
    title: "Compliance",
    image:
      "https://asset.sparkl.me/pb/poc-folder/Fxfsj569MQ0j5f1qSPpQt1ourMuBM15bP78SJgf7.jpg",
    color: "mint",
    x: 150,
    y: 0,
    minutes: 10,
    steps: 7,
  },
  {
    title: "The Art of Selling",
    image:
      "https://asset.sparkl.me/pb/poc-folder/t6HpblDYD66PJ54sNqo9Vgn1siSCSU3Cjy59Sv4Q.jpg",
    color: "amber",
    x: 450,
    y: 0,
    minutes: 14,
    steps: 6,
  },
];

/** Tutor carousel — count is used to snap one full slide per scroll segment. */
const TUTOR_SLIDES = [
  {
    eyebrow: "Module 1",
    image:
      "https://asset.sparkl.me/pb/poc-folder/wRILGudRLGmSkja1YsM4bjJAq2nAvjQl3YnZCKWR.png",
  },
  {
    eyebrow: "Module 1 · Concepts",
    image:
      "https://asset.sparkl.me/pb/poc-folder/xfEDCPHiWoDznIEi0yJDDvqU4mnH3iWeZhoRNzB7.png",
  },
  {
    eyebrow: "Module 1 · Case study",
    image:
      "https://asset.sparkl.me/pb/poc-folder/vY1YKCpe9qfKngs3ZFOvXG5NWehI4U20odDcLija.png",
  },
];

const SCRUB_TUTOR_BEGIN = 0.43;
const SCRUB_TUTOR_END = 0.55;

const SCROLLY_NARROW_MQ = "(max-width: 767px)";

/** Gap between narrow 2×2 session cards — pairs with `gap-*` on that grid (Tailwind gap-4 = 16px). */
const NARROW_SESSION_GRID_GAP_PX = 16;

/** Same as canvas wrapper: scale(clamp(0.32, (100vw - 24px) / 1100, 1)). */
function getScrollyCanvasScale(vw) {
  return Math.min(1, Math.max(0.32, (vw - 24) / 1100));
}

/** Layout width (pre-scale canvas px) so on-screen width ≤ 35vw; also fits 2 cols in 1100 canvas. */
function getNarrowSessionCardLayoutWidth(vw) {
  const s = getScrollyCanvasScale(vw);
  const maxOnScreen35vw = (0.35 * vw) / s;
  const gridPad = 24; // px-3 ×2
  const gap = NARROW_SESSION_GRID_GAP_PX;
  const maxHalfCanvas = (1100 - gridPad - gap) / 2;
  return Math.min(maxOnScreen35vw, maxHalfCanvas);
}

/**
 * GSAP x/y (local px, before outer canvas scale) so the session card center meets
 * the viewport center — same idea as desktop `x: -focusOffsetX`, for narrow 2×2 grid.
 */
function getFocusPanToViewportCenter(el, vw, vh) {
  const rect = el.getBoundingClientRect();
  const s = getScrollyCanvasScale(vw);
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const vx = vw / 2;
  const vy = vh / 2;
  return {
    x: (vx - cx) / s,
    y: (vy - cy) / s,
  };
}

// =============================================================
export default function Scrollytelling() {
  const rootRef = useRef(null);
  const [activeStage, setActiveStage] = useState(0);
  const [slideProgress, setSlideProgress] = useState(0);
  const [flagCount, setFlagCount] = useState(0);
  const [recertActive, setRecertActive] = useState(false);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(SCROLLY_NARROW_MQ);
    const onNarrow = () => setNarrow(mq.matches);
    onNarrow();
    mq.addEventListener("change", onNarrow);
    return () => mq.removeEventListener("change", onNarrow);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) return;

    const mm = gsap.matchMedia();

    mm.add("(min-width: 320px)", () => {
      const root = rootRef.current;
      if (!root) return;

      const files = root.querySelectorAll(".s-file");
      const engine = root.querySelector(".s-engine");
      const engineRings = root.querySelectorAll(".s-engine-ring");
      const engineLabel = root.querySelector(".s-engine-label");
      const sessions = root.querySelectorAll(".s-session");
      const focusSession = root.querySelector(".s-session-focus");
      const nonFocusSessions = Array.from(sessions).filter(
        (el) => !el.classList.contains("s-session-focus")
      );
      const tutorUI = root.querySelector(".s-tutor");
      const analyticsUI = root.querySelector(".s-analytics");

      // Measure each file's translation vector toward the AI engine center.
      // IMPORTANT: on mobile, the canvas is inside a CSS `transform: scale(...)`
      // wrapper. getBoundingClientRect() returns POST-scale viewport pixels,
      // but GSAP applies `x/y` transforms in the element's LOCAL (pre-scale)
      // coord space. We compensate by dividing the deltas by the current scale
      // so files land on the engine visually correctly at any viewport width.
      const engineRect = engine.getBoundingClientRect();
      const scaleX = engine.offsetWidth
        ? engineRect.width / engine.offsetWidth
        : 1;
      const engineCx = engineRect.left + engineRect.width / 2;
      const engineCy = engineRect.top + engineRect.height / 2;
      const fileTargets = Array.from(files).map((el) => {
        const r = el.getBoundingClientRect();
        return {
          dx: (engineCx - (r.left + r.width / 2)) / scaleX,
          dy: (engineCy - (r.top + r.height / 2)) / scaleX,
        };
      });

      gsap.set(files, {
        opacity: 0,
        y: () => gsap.utils.random(80, 160),
        x: () => gsap.utils.random(-60, 60),
        rotation: () => gsap.utils.random(-18, 18),
        scale: 0.75,
      });
      gsap.set(engine, { opacity: 0, scale: 0.2 });
      gsap.set(engineRings, { opacity: 0, scale: 0.6 });
      gsap.set(engineLabel, { opacity: 0, y: 8 });
      // Sessions: wide layout is absolutely positioned at calc(50%±…); xPercent/yPercent
      // center those boxes so GSAP x/y adds cleanly. Narrow is a 2×2 grid — same
      // translate(-50%,-50%) shifts every cell up-left and pulls the block left.
      gsap.set(sessions, {
        ...(narrow
          ? { xPercent: 0, yPercent: 0, x: 0, y: 0 }
          : { xPercent: -50, yPercent: -50 }),
        opacity: 0,
        scale: 0.88,
      });
      gsap.set(tutorUI, { opacity: 0, scale: 1, y: 0 });
      gsap.set(analyticsUI, { opacity: 0, scale: 0.6, y: 0 });

      const narrowFocusPan = { x: 0, y: 0 };
      let narrowPanNeedsRefresh = true;
      const refreshNarrowFocusPan = () => {
        if (!narrow || !focusSession) return;
        const pan = getFocusPanToViewportCenter(
          focusSession,
          window.innerWidth,
          window.innerHeight
        );
        narrowFocusPan.x = pan.x;
        narrowFocusPan.y = pan.y;
      };

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root,
          start: "top top",
          end: "+=1500%",
          scrub: 1.0,
          // Pin the whole section (gradient lives on <section>), not the inner
          // flex column — otherwise the header fixes while the blue bg keeps scrolling.
          pin: true,
          pinSpacing: true,
          anticipatePin: 0,
          // Force `pinType: "fixed"` on touch/mobile devices — the default
          // "transform" pin type is broken in iOS Safari (loses fixed
          // positioning while scrolling momentum is active).
          pinType: "fixed",
          invalidateOnRefresh: true,
          onRefresh: refreshNarrowFocusPan,
          onUpdate: (self) => {
            const p = self.progress;
            // Measure once sessions are on-screen (pinned) before focus zoom so pan
            // matches viewport — not layout from effect init before scroll.
            if (narrow && focusSession && p >= 0.32 && narrowPanNeedsRefresh) {
              refreshNarrowFocusPan();
              narrowPanNeedsRefresh = false;
            }
            if (p < 0.28) narrowPanNeedsRefresh = true;

            setActiveStage(progressToStage(p));

            // Tutor slides: 0.43 → 0.55. Snap to one full slide per equal scroll
            // segment (no half-advance between images).
            const nSlides = TUTOR_SLIDES.length;
            if (p >= SCRUB_TUTOR_BEGIN && p < SCRUB_TUTOR_END) {
              const sub =
                (p - SCRUB_TUTOR_BEGIN) /
                (SCRUB_TUTOR_END - SCRUB_TUTOR_BEGIN);
              const idx = Math.min(
                nSlides - 1,
                Math.floor(sub * nSlides)
              );
              setSlideProgress(idx);
            } else if (p < SCRUB_TUTOR_BEGIN) {
              setSlideProgress(0);
            } else {
              setSlideProgress(nSlides - 1);
            }

            // Sequential highlights AFTER the zoom crossfade completes:
            //   0.67 → 0.78 : Dan R.    (flagCount = 1, ~2 viewports)
            //   0.78 → 0.89 : Marcus T. (flagCount = 2, ~2 viewports)
            //   0.89 → 1.00 : Re-certify activates (~2 viewports + hold)
            let fc = 0;
            if (p >= 0.67 && p < 0.78) fc = 1;
            else if (p >= 0.78) fc = 2;
            setFlagCount(fc);

            setRecertActive(p >= 0.89);
          },
        },
      });

      // ── 0.00-0.01  Intro blank ─────────────────────────────────
      tl.to({}, { duration: 0.01 });

      // ── 0.01-0.06  STEP 01: Files pop in ───────────────────────
      tl.to(
        files,
        {
          opacity: 1,
          y: 0,
          x: 0,
          rotation: (i) => [-8, 6, -4, 10, -2, 5, -6, 3, -10][i % 9],
          scale: 1,
          stagger: { each: 0.005, from: "random" },
          duration: 0.05,
          ease: "power3.out",
        },
        0.01
      );

      // ── 0.06-0.10  BLANK HOLD (files just sit there) ───────────
      tl.to({}, { duration: 0.04 });

      // ── 0.10-0.13  STEP 02: AI core + label appear ─────────────
      tl.to(engine, { opacity: 1, scale: 1, duration: 0.030, ease: "power2.out" }, 0.10);
      tl.to(engineRings, { opacity: 1, scale: 1, stagger: 0.0075, duration: 0.030 }, 0.11);
      tl.to(engineLabel, { opacity: 1, y: 0, duration: 0.025, ease: "power2.out" }, 0.12);

      // ── 0.13-0.21  Files travel INTO the AI ────────────────────
      tl.to(
        files,
        {
          x: (i) => fileTargets[i]?.dx ?? 0,
          y: (i) => fileTargets[i]?.dy ?? 0,
          rotation: 0,
          scale: 0.12,
          opacity: 0,
          stagger: { each: 0.007, from: "random" },
          duration: 0.08,
          ease: "power2.in",
        },
        0.13
      );

      // ── 0.21-0.25  AI GLOWS intensely (build-up) ───────────────
      tl.to(engine, { scale: 1.22, duration: 0.04, ease: "power2.out" }, 0.21);
      tl.to(
        engineRings,
        { opacity: 1, scale: 1.18, stagger: 0.01, duration: 0.04, ease: "power2.out" },
        0.21
      );
      tl.to(engineLabel, { scale: 1.05, duration: 0.04, ease: "power2.out" }, 0.21);

      // ── 0.25-0.28  GLOW PEAK HOLD ──────────────────────────────
      tl.to(engine, { scale: 1.22, duration: 0.03, ease: "sine.inOut" }, 0.25);

      // ── 0.28-0.30  Engine + rings + label fade out cleanly ─────
      tl.to(engine, { scale: 0.6, opacity: 0, duration: 0.02, ease: "power2.in" }, 0.28);
      tl.to(engineRings, { opacity: 0, scale: 1.35, duration: 0.02, ease: "power2.in" }, 0.28);
      tl.to(engineLabel, { opacity: 0, y: 8, scale: 1, duration: 0.02, ease: "power2.in" }, 0.28);

      // ── 0.30-0.35  STEP 03: Sessions emerge one by one ─────────
      tl.to(
        sessions,
        {
          opacity: 1,
          scale: 1,
          stagger: 0.011,
          duration: 0.025,
          ease: "power3.out",
        },
        0.30
      );

      // ── 0.35-0.37  BLANK HOLD (all 4 sessions visible) ─────────
      tl.to({}, { duration: 0.02 });

      // ── 0.37-0.39  STEP 04a: Non-focus cards fade out ──────────
      tl.to(
        nonFocusSessions,
        { opacity: 0, scale: 0.82, duration: 0.02, ease: "power2.in" },
        0.37
      );

      // ── 0.39-0.43  STEP 04b: Focus zooms up + Tutor UI fades in ─
      if (focusSession) {
        const focusOffsetX = parseFloat(focusSession.dataset.offsetX || 0);
        const focusZoom = narrow ? 2.35 : 4.5;
        refreshNarrowFocusPan();
        tl.to(
          focusSession,
          {
            scale: focusZoom,
            x: () => (narrow ? narrowFocusPan.x : -focusOffsetX),
            y: () => (narrow ? narrowFocusPan.y : 0),
            opacity: 0,
            duration: 0.04,
            ease: "power2.inOut",
          },
          0.39
        );
      }
      tl.to(
        tutorUI,
        { opacity: 1, scale: 1, duration: 0.04, ease: "power2.inOut" },
        0.39
      );

      // ── 0.43-0.55  Slides play (3 slides over ~2.4 viewports of scroll) ─
      tl.to({}, { duration: 0.12 });

      // ── 0.55-0.67  Zoom CROSSFADE: tutor zooms OUT + analytics zooms IN ─
      // Window 0.12 of timeline × 1500% end ≈ 1.8 viewports of scroll
      // → feels like "2 scrolls" to fully hand off to the insights dashboard.
      tl.to(
        tutorUI,
        { opacity: 0, scale: 0.55, y: -10, duration: 0.12, ease: "power2.inOut" },
        0.55
      );
      tl.to(
        analyticsUI,
        { opacity: 1, scale: 1, y: 0, duration: 0.12, ease: "power2.inOut" },
        0.55
      );

      // ── 0.67-1.00  Trailing space for Dan/Marcus/Recert highlights ─
      // These highlights are driven by React state via onUpdate (not timeline
      // tweens), so the timeline just needs EMPTY duration here so that its
      // total length equals 1.0 → scrollTrigger's `scrub` maps scroll progress
      // 0-1 directly onto my `position` values (0.55, 0.67, etc.).
      tl.to({}, { duration: 0.33 });
    });

    return () => mm.revert();
  }, [narrow]);

  const copy = STAGE_COPY[activeStage];

  return (
    <section
      id="how-it-works"
      ref={rootRef}
      data-testid="scrollytelling-section"
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 120% 70% at 50% 0%, #1e1b4b 0%, transparent 60%)," +
          "radial-gradient(ellipse 80% 60% at 0% 100%, #0b1026 0%, transparent 70%)," +
          "radial-gradient(ellipse 80% 60% at 100% 100%, #0a1e2f 0%, transparent 70%)," +
          "linear-gradient(180deg, #06091a 0%, #0a0e20 50%, #0b0820 100%)",
      }}
    >
      {/* Pinned experience – title on top, full-width animation below (all widths) */}
      <div className="relative flex min-h-screen h-screen w-full overflow-hidden flex-col items-center justify-start pt-14 sm:pt-16 xl:pt-20 pb-6">
        <div
          className="blob"
          style={{
            width: 520,
            height: 520,
            top: -80,
            left: -80,
            background:
              "radial-gradient(circle, rgba(59,130,246,0.55), transparent 70%)",
          }}
        />
        <div
          className="blob"
          style={{
            width: 560,
            height: 560,
            bottom: -140,
            right: -120,
            background:
              "radial-gradient(circle, rgba(139,92,246,0.55), transparent 70%)",
          }}
        />

        {/* ── TOP HEADER BLOCK ───────────────────────────────── */}
        <div
          className="relative z-20 w-full max-w-5xl px-4 sm:px-8 text-center"
          data-testid="scrolly-header"
        >
          <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.22em] sm:tracking-[0.24em] text-cyan-300">
            <span className="w-4 sm:w-5 h-px bg-gradient-to-r from-transparent to-cyan-400" />
            How Sparkl AI works
            <span className="w-4 sm:w-5 h-px bg-gradient-to-l from-transparent to-cyan-400" />
          </div>

          {/* ── Active step title (eye-catching, dynamic) ── */}
          <div
            key={activeStage}
            className="fade-up mt-3 sm:mt-4 flex flex-col sm:flex-row items-center sm:items-center justify-center gap-2 sm:gap-5"
            style={{ animationDuration: "0.55s" }}
            data-testid="scrolly-active-step-title"
          >
            <span
              className="inline-block text-[44px] sm:text-[56px] xl:text-[68px] font-black leading-none bg-clip-text text-transparent tracking-tight tabular-nums"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #60a5fa 0%, #a78bfa 45%, #f472b6 100%)",
                textShadow: "0 10px 40px rgba(99,102,241,0.4)",
              }}
            >
              {STEPS[activeStage].n}
            </span>
            <div className="flex flex-col items-center sm:items-start text-center sm:text-left">
              <h3 className="text-[18px] sm:text-[26px] xl:text-[32px] leading-[1.12] font-extrabold tracking-tight text-white text-balance max-w-[720px]">
                {copy.title}
              </h3>
              <span
                className="mt-2 h-[3px] rounded-full"
                style={{
                  width: "80px",
                  background:
                    "linear-gradient(90deg, #60a5fa 0%, #a78bfa 50%, #f472b6 100%)",
                  animation: "stepUnderline 0.6s ease-out both",
                }}
              />
              <p className="mt-2 sm:mt-3 text-[12.5px] sm:text-[14.5px] xl:text-[15.5px] leading-relaxed text-slate-300 max-w-[620px] hidden sm:block">
                {copy.body}
              </p>
            </div>
          </div>
        </div>

        {/* ── FULL-WIDTH ANIMATION CANVAS (auto-scales on mobile) ─ */}
        <div className="relative z-10 mt-6 xl:mt-8 flex min-h-0 flex-1 w-full min-w-0 items-center justify-center overflow-hidden">
          {/*
            Fixed 1100px logical width + shrink-0 + mx-auto so flex centers the
            pre-scale box; transform scale paints from center without drifting left.
          */}
          <div
            className="mx-auto w-[1100px] max-w-[1100px] shrink-0"
            style={{
              transform:
                "scale(clamp(0.32, calc((100vw - 24px) / 1100px), 1))",
              transformOrigin: "center center",
            }}
          >
            <ScrollyCanvas
              narrow={narrow}
              slideProgress={slideProgress}
              flagCount={flagCount}
              recertActive={recertActive}
              tutorActive={activeStage === 3}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================
// Canvas (now 1.5x bigger and wider for horizontal sessions)
// =============================================================
function ScrollyCanvas({
  narrow = false,
  slideProgress,
  flagCount,
  recertActive,
  tutorActive = false,
}) {
  const [narrowCardLayoutW, setNarrowCardLayoutW] = useState(() =>
    typeof window !== "undefined" ? getNarrowSessionCardLayoutWidth(window.innerWidth) : 212
  );

  useLayoutEffect(() => {
    if (!narrow) return;
    const ro = () => setNarrowCardLayoutW(getNarrowSessionCardLayoutWidth(window.innerWidth));
    ro();
    window.addEventListener("resize", ro);
    return () => window.removeEventListener("resize", ro);
  }, [narrow]);

  const narrowGridWidth =
    narrow ? narrowCardLayoutW * 2 + NARROW_SESSION_GRID_GAP_PX : undefined;

  return (
    <div
      className="relative"
      style={{ width: 1100, height: 560 }}
    >
      {/* No backdrop card – canvas is naked and spans full width */}

      {/* Files layer — slightly inset so larger tiles stay inside the canvas */}
      <div className="absolute inset-0">
        {[
          { Icon: FileText, label: "PDF", tone: "blue", top: "10%", left: "10%" },
          { Icon: Presentation, label: "PPT", tone: "amber", top: "24%", left: "6%" },
          { Icon: Video, label: "VIDEO", tone: "violet", top: "46%", left: "7%" },
          { Icon: BookOpen, label: "SOP", tone: "mint", top: "68%", left: "11%" },
          { Icon: FileSpreadsheet, label: "SHEET", tone: "rose", top: "82%", left: "24%" },
          { Icon: FileCode2, label: "DOC", tone: "blue", top: "8%", right: "12%" },
          { Icon: ShieldCheck, label: "POLICY", tone: "violet", top: "26%", right: "7%" },
          { Icon: FileText, label: "PDF", tone: "amber", top: "52%", right: "8%" },
          { Icon: Video, label: "VIDEO", tone: "mint", top: "74%", right: "16%" },
        ].map((f, i) => (
          <div
            key={i}
            className="s-file absolute"
            style={{ top: f.top, left: f.left, right: f.right }}
          >
            <FileTileSmall icon={f.Icon} label={f.label} tone={f.tone} />
          </div>
        ))}
      </div>

      {/* Central AI engine */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="s-engine relative">
          <AICoreVisual size={narrow ? 236 : 220} />
          <div
            className="s-engine-ring absolute inset-[-28px] rounded-full"
            style={{ border: "1px dashed rgba(99,102,241,0.35)" }}
          />
          <div
            className="s-engine-ring absolute inset-[-56px] rounded-full"
            style={{ border: "1px dashed rgba(139,92,246,0.25)" }}
          />
          <div
            className="s-engine-ring absolute inset-[-86px] rounded-full"
            style={{ border: "1px dashed rgba(59,130,246,0.18)" }}
          />
        </div>
      </div>

      {/* AI Engine label — mobile: sits below canvas (−3rem); sm+: flush with canvas bottom */}
      <div className="absolute inset-x-0 bottom-[-3rem] sm:bottom-0 z-[5] flex justify-center pointer-events-none px-3">
        <div className="s-engine-label max-sm:pt-[4rem] text-center max-w-[min(100%,520px)]">
          <div className="text-[20px] sm:text-xs font-bold uppercase tracking-[0.24em] text-cyan-300 mb-2.5 sm:mb-1.5">
            Sparkl’s
          </div>
          <div className="text-[48px] sm:text-[34px] font-extrabold tracking-tight text-white leading-[1.02] sm:leading-[1.1]">
            AI Engine
          </div>
        </div>
      </div>

      {/* Sessions — wide: horizontal rail · narrow: 2×2 centered in full canvas width */}
      <div
        className={`absolute inset-0 flex items-center justify-center pointer-events-none ${narrow ? "px-3" : ""}`}
      >
        {narrow ? (
          <div className="flex w-full max-w-full items-center justify-center">
            <div
              className="grid grid-cols-2 gap-4 justify-items-center shrink-0 z-[1]"
              style={{
                width: narrowGridWidth,
                maxWidth: "100%",
              }}
            >
              {SESSIONS.map((s) => (
                <div
                  key={s.title}
                  className={`s-session flex w-full min-w-0 justify-center ${s.focus ? "s-session-focus" : ""}`}
                  data-offset-x={0}
                  data-offset-y={0}
                >
                  <SessionCardVisual
                    title={s.title}
                    color={s.color}
                    image={s.image}
                    minutes={s.minutes}
                    steps={s.steps}
                    compact
                    layoutWidth={narrowCardLayoutW}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="relative w-[880px] h-[360px]">
            {SESSIONS.map((s) => (
              <div
                key={s.title}
                className={`absolute s-session ${s.focus ? "s-session-focus" : ""}`}
                data-offset-x={s.x}
                data-offset-y={s.y}
                style={{
                  left: `calc(50% + ${s.x}px)`,
                  top: `calc(50% + ${s.y}px)`,
                }}
              >
                <SessionCardVisual
                  title={s.title}
                  color={s.color}
                  image={s.image}
                  minutes={s.minutes}
                  steps={s.steps}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tutor UI */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="s-tutor">
          <TutorUIMock slideProgress={slideProgress} tutorActive={tutorActive} />
        </div>
      </div>

      {/* Analytics UI */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="s-analytics">
          <AnalyticsUIMock flagCount={flagCount} recertActive={recertActive} />
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Primitives
// =============================================================
function FileTileSmall({ icon: Icon, label, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    mint: "bg-teal-50 text-teal-700 border-teal-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div
      className={`${tones[tone]} file-tile-shadow border rounded-xl w-[98px] h-[120px] p-2.5 flex flex-col justify-between bg-white`}
    >
      <div className="flex items-center justify-between">
        <Icon size={17} strokeWidth={2.2} />
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
      </div>
      <div className="space-y-1">
        <div className="h-1.5 rounded-full bg-current opacity-20 w-4/5" />
        <div className="h-1.5 rounded-full bg-current opacity-15 w-3/5" />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-wider leading-tight">
        {label}
      </div>
    </div>
  );
}

function SessionCardVisual({
  title,
  color = "blue",
  image,
  minutes,
  steps,
  compact = false,
  layoutWidth,
}) {
  const colorMap = {
    blue: "text-blue-700 bg-blue-50 border-blue-200",
    violet: "text-violet-700 bg-violet-50 border-violet-200",
    mint: "text-teal-700 bg-teal-50 border-teal-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
  };
  const wrap =
    layoutWidth != null
      ? "w-full max-w-full"
      : compact
        ? "w-[212px] max-w-full"
        : "w-[240px]";
  const wrapStyle =
    layoutWidth != null ? { width: layoutWidth, maxWidth: "100%" } : undefined;
  return (
    <div
      className={`${wrap} bg-white rounded-2xl border border-slate-200 shadow-[0_20px_44px_-18px_rgba(15,23,42,0.22)] overflow-hidden`}
      style={wrapStyle}
    >
      {image && (
        <div
          className={
            compact || layoutWidth != null
              ? "relative w-full aspect-[16/10] bg-slate-100 overflow-hidden shrink-0"
              : "relative w-full aspect-[16/10] bg-slate-100 overflow-hidden"
          }
        >
          <img
            src={image}
            alt={title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,23,42,0) 55%, rgba(15,23,42,0.25) 100%)",
            }}
          />
        </div>
      )}

      <div className={compact ? "p-3" : "p-4"}>
        <div
          className={`inline-flex items-center gap-1.5 font-bold uppercase tracking-wider rounded-full border ${colorMap[color]} ${compact ? "text-[9px] px-2 py-0.5 gap-1" : "text-[10px] px-2.5 py-1"}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />{" "}
          Session
        </div>
        <div
          className={`font-bold text-slate-900 leading-tight tracking-tight ${compact ? "mt-2 text-[13px] line-clamp-2" : "mt-2.5 text-[15px]"}`}
        >
          {title}
        </div>
        <div className={`flex items-center justify-between ${compact ? "mt-2" : "mt-3"}`}>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="w-5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 shrink-0" />
            <span
              className={`font-semibold text-slate-500 truncate ${compact ? "text-[9.5px]" : "text-[10.5px]"}`}
            >
              {minutes} min · {steps} steps
            </span>
          </div>
          <span
            className={`font-bold text-blue-600 shrink-0 ${compact ? "text-[9.5px]" : "text-[10.5px]"}`}
          >
            Start →
          </span>
        </div>
      </div>
    </div>
  );
}

function AICoreVisual({ size = 200 }) {
  const s = `${size}px`;
  return (
    <div
      className="relative rounded-[28px] ai-core-glow overflow-hidden"
      style={{
        width: s,
        height: s,
        background:
          "linear-gradient(160deg, #ffffff 0%, #f1f5ff 50%, #ede9fe 100%)",
        border: "1px solid rgba(99,102,241,0.18)",
      }}
    >
      <div className="absolute inset-0 bg-soft-grid opacity-[0.35]" />
      <div className="absolute inset-0 flex items-center justify-center">
        <MascotOrb size={Math.floor(size * 0.58)} />
      </div>
      <div className="absolute inset-0">
        {[
          { top: "14%", left: "20%" },
          { top: "22%", right: "18%" },
          { bottom: "18%", left: "24%" },
          { bottom: "22%", right: "22%" },
        ].map((p, i) => (
          <span
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-blue-500 animate-soft-pulse"
            style={{ ...p, animationDelay: `${i * 0.4}s` }}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================
// Tutor UI as a PPT-style slide deck (TUTOR_SLIDES lives near SESSIONS)
// =============================================================
function TutorUIMock({ slideProgress = 0, tutorActive = false }) {
  const safeIndex = Math.min(
    Math.max(0, slideProgress),
    TUTOR_SLIDES.length - 1
  );
  const slide = TUTOR_SLIDES[safeIndex];

  return (
    <div
      className="relative"
      data-testid="tutor-ui-mock"
      style={{ width: 860, height: 520 }}
    >
      {/* Outer box with padding; slides flip on scroll (step 4 tutor segment) */}
      <div className="relative w-full h-full rounded-[28px] overflow-hidden shadow-[0_30px_80px_-30px_rgba(15,23,42,0.45)] bg-white border border-slate-200 p-5 sm:p-6">
        <div className="relative h-full w-full overflow-hidden rounded-2xl bg-slate-50 [perspective:1200px]">
          <div className="relative h-full w-full min-h-[180px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={safeIndex}
                initial={{ rotateY: 85, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                exit={{ rotateY: -85, opacity: 0 }}
                transition={{
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  transformStyle: "preserve-3d",
                  backfaceVisibility: "hidden",
                }}
              >
                <img
                  src={slide.image}
                  alt={`Slide ${safeIndex + 1}`}
                  loading="lazy"
                  className="max-h-full max-w-full h-auto w-auto object-contain rounded-xl"
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Speaking mascot — sits at the top-left, overlapping the slide corner */}
      <div
        className="absolute z-20 flex items-center justify-center pointer-events-none"
        style={{
          width: 120,
          height: 120,
          top: -60,
          left: -60,
        }}
        data-testid="tutor-mascot"
      >
        <SpeakingMascot size={120} />
      </div>

      {/* Trainee video card — 16:9, 30% overlap bottom-right */}
      <AnimatePresence>
        {tutorActive && (
          <motion.div
            key="trainee-video"
            initial={{ opacity: 0, y: 14, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.92 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="absolute rounded-xl overflow-hidden bg-slate-900 shadow-[0_20px_44px_-16px_rgba(15,23,42,0.55)] border-[3px] border-white"
            style={{
              width: 160,
              height: 90, // 16:9
              // 30% overlap: 70% sticks out → shift outward by 112px (70% of 160)
              right: -112,
              bottom: -63, // 70% of 90
            }}
            data-testid="trainee-video-card"
          >
            <img
              src={traineeAvatar}
              alt="Trainee"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 h-9 bg-gradient-to-t from-black/75 to-transparent" />

            <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white bg-rose-500 rounded-full px-1.5 py-0.5">
              <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
              Rec
            </span>

            <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
              <span className="flex items-end gap-[1.5px] h-2.5">
                <span className="w-[1.5px] bg-white rounded-full voice-wave-bar" style={{ height: "100%" }} />
                <span className="w-[1.5px] bg-white rounded-full voice-wave-bar" style={{ height: "100%", animationDelay: "0.2s" }} />
                <span className="w-[1.5px] bg-white rounded-full voice-wave-bar" style={{ height: "100%", animationDelay: "0.4s" }} />
              </span>
            </span>

            <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between text-white">
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] leading-none">
                Trainee
              </span>
              <span className="text-[9px] font-semibold opacity-90 tabular-nums">
                12:34
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Speaking mascot — concentric circular ripples only, no radial bars
function SpeakingMascot({ size = 120 }) {
  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* Soft violet halo */}
      <span
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: -10,
          background:
            "radial-gradient(circle, rgba(139,92,246,0.45), rgba(139,92,246,0) 65%)",
        }}
      />

      {/* Concentric pulsing ripples (3 staggered) */}
      <span className="tutor-speak-ring absolute rounded-full" style={{ inset: 8 }} />
      <span
        className="tutor-speak-ring absolute rounded-full"
        style={{ inset: 8, animationDelay: "0.6s" }}
      />
      <span
        className="tutor-speak-ring absolute rounded-full"
        style={{ inset: 8, animationDelay: "1.2s" }}
      />

      {/* The mascot itself on top */}
      <div className="relative z-10">
        <MascotOrb size={Math.round(size * 0.7)} />
      </div>
    </div>
  );
}

function IntroSlideBody() {
  return (
    <div className="h-full grid grid-cols-[1fr_1fr] gap-6 items-center">
      <ul className="space-y-4">
        {[
          "Keep customers on the product.",
          "Protect revenue and brand trust.",
          "Unlock faster incident recovery.",
        ].map((b, i) => (
          <li key={b} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex items-center justify-center w-6 h-6 rounded-full text-white text-[11px] font-bold flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #2563eb, #7c3aed)",
              }}
            >
              {i + 1}
            </span>
            <span className="text-[15px] text-slate-700 leading-relaxed">{b}</span>
          </li>
        ))}
      </ul>
      <div className="rounded-xl border border-slate-100 bg-slate-50 h-full flex items-center justify-center">
        <div className="text-center">
          <div
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-3"
            style={{
              background: "linear-gradient(135deg, #2563eb, #7c3aed)",
            }}
          >
            <BarChart3 size={28} strokeWidth={2} />
          </div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
            Why it matters
          </div>
        </div>
      </div>
    </div>
  );
}

function PillarsSlideBody({ pillars }) {
  const toneMap = {
    blue: "from-blue-500 to-indigo-500",
    violet: "from-violet-500 to-fuchsia-500",
    mint: "from-teal-500 to-emerald-500",
  };
  return (
    <div className="h-full grid grid-cols-3 gap-4">
      {pillars.map((p) => {
        const Icon = p.icon;
        return (
          <div
            key={p.title}
            className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 flex flex-col"
          >
            <div
              className={`w-11 h-11 rounded-lg flex items-center justify-center text-white bg-gradient-to-br ${toneMap[p.tone]}`}
            >
              <Icon size={22} strokeWidth={2.2} />
            </div>
            <div className="mt-3 text-[16px] font-bold text-slate-900 leading-tight">
              {p.title}
            </div>
            <div className="mt-1 text-[13px] text-slate-600 leading-snug">
              {p.body}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatsSlideBody({ stats, quote }) {
  return (
    <div className="h-full flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-slate-100 bg-white p-3.5"
          >
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {s.label}
            </div>
            <div
              className="mt-1 text-[26px] font-extrabold tracking-tight bg-clip-text text-transparent leading-tight tabular-nums"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
              }}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>
      <div className="flex-1 rounded-xl bg-gradient-to-br from-blue-50/80 to-violet-50/80 border border-blue-100 px-5 py-4 flex items-center">
        <div className="text-[15px] italic text-slate-700 leading-relaxed">
          “{quote}”
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Analytics UI
// =============================================================
const LEARNER_ROWS = [
  { name: "Alex M.", score: 94, status: "ok" },
  { name: "Priya S.", score: 88, status: "ok" },
  { name: "Dan R.", score: 48, status: "flag", topic: "Policy" },
  { name: "Sofia L.", score: 90, status: "ok" },
  { name: "Marcus T.", score: 42, status: "flag", topic: "Compliance" },
  { name: "Ira N.", score: 55, status: "flag", topic: "Policy" },
];

function AnalyticsUIMock({ flagCount = 0, recertActive = false }) {
  const flagsShown = LEARNER_ROWS.map((r) => ({ ...r, active: false }));
  let seen = 0;
  for (let i = 0; i < flagsShown.length; i++) {
    if (flagsShown[i].status === "flag" && seen < flagCount) {
      flagsShown[i].active = true;
      seen++;
    }
  }

  return (
    <div
      className="relative bg-white rounded-2xl shadow-[0_30px_80px_-30px_rgba(15,23,42,0.3)] border border-slate-200 overflow-hidden w-[860px] h-[520px] xl:w-[980px] xl:h-[580px]"
      data-testid="analytics-ui-mock"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-rose-400" />
          <div className="w-2 h-2 rounded-full bg-amber-400" />
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[13px] font-bold text-slate-700">
            Trainer dashboard
          </span>
        </div>
        <div className="text-[12px] font-semibold text-slate-500">
          This week
        </div>
      </div>

      <div className="grid grid-cols-[180px_1fr] h-[calc(100%-42px)]">
        <div className="border-r border-slate-100 bg-slate-50/50 p-4 flex flex-col gap-1">
          {[
            { label: "Overview", active: true },
            { label: "Sessions" },
            { label: "Learners" },
            { label: "Insights" },
          ].map((it) => (
            <div
              key={it.label}
              className={`text-[13px] px-3 py-2 rounded-lg font-medium ${
                it.active
                  ? "bg-white text-slate-900 shadow-sm border border-slate-100"
                  : "text-slate-500"
              }`}
            >
              {it.label}
            </div>
          ))}
        </div>

        <div className="p-4 space-y-3 overflow-hidden">
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Completion" value="87%" tone="emerald" />
            <StatCard label="Engagement" value="74%" tone="violet" />
            <StatCard
              label="At risk"
              value={flagCount}
              tone="rose"
              pulse={flagCount > 0}
            />
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Learners · policy comprehension
              </div>
              <div className="text-[11px] text-slate-400">Score</div>
            </div>
            <div className="divide-y divide-slate-100">
              {flagsShown.slice(0, 5).map((row) => {
                const showFlag = row.status === "flag" && row.active;
                return (
                  <div
                    // Changing key when `showFlag` flips re-mounts the row,
                    // which re-fires the pulse keyframe animation.
                    key={`${row.name}-${showFlag ? "on" : "off"}`}
                    className={`flex items-center justify-between py-1.5 px-2 rounded-md ${
                      showFlag ? "bg-rose-50/80 flag-row-pulse" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                          showFlag
                            ? "bg-gradient-to-br from-rose-500 to-red-600"
                            : "bg-gradient-to-br from-blue-400 to-violet-500"
                        }`}
                      >
                        {row.name.charAt(0)}
                      </div>
                      <span className="text-[12.5px] font-semibold text-slate-800">
                        {row.name}
                      </span>
                      {showFlag && (
                        <span className="flag-badge-in inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 bg-rose-100 border border-rose-200 px-1.5 py-0.5 rounded-full">
                          <AlertTriangle size={10} strokeWidth={2.5} />
                          {row.topic}
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-[12.5px] font-bold tabular-nums ${
                        showFlag ? "text-rose-600" : "text-slate-600"
                      }`}
                    >
                      {row.score}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            // Re-key on recertActive so the wake animation fires on transition
            key={`recert-${recertActive ? "on" : "off"}`}
            className={`rounded-xl border px-4 py-3.5 flex items-center gap-4 transition-all ${
              recertActive
                ? "border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-[0_10px_24px_-12px_rgba(245,158,11,0.5)] recert-wake"
                : "border-slate-200 bg-white opacity-70"
            }`}
            data-testid="recert-card"
          >
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                recertActive
                  ? "bg-white border border-amber-200 shadow-sm"
                  : "bg-slate-50 border border-slate-200"
              }`}
            >
              <RefreshCcw
                size={20}
                className={`${recertActive ? "text-amber-600" : "text-slate-400"}`}
              />
            </div>
            <div className="flex-1">
              <div
                className={`text-[14px] font-bold ${
                  recertActive ? "text-amber-900" : "text-slate-700"
                }`}
              >
                Re-certify {flagCount || 2} learners in policy
              </div>
              <div
                className={`text-[12px] mt-0.5 ${
                  recertActive ? "text-amber-700" : "text-slate-500"
                }`}
              >
                Recommended action · send a 10-minute reinforcement session
              </div>
            </div>
            <button
              className={`text-[12.5px] font-semibold px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                recertActive
                  ? "text-white shadow-sm"
                  : "bg-slate-100 text-slate-400"
              }`}
              style={
                recertActive
                  ? {
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                    }
                  : {}
              }
            >
              Re-certify
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, pulse = false }) {
  const toneMap = {
    emerald: "text-emerald-600",
    violet: "text-violet-600",
    rose: "text-rose-600",
  };
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        // Re-key on value so the bump keyframe fires each time count changes
        key={`v-${value}`}
        className={`mt-1 text-[28px] font-extrabold leading-tight tabular-nums ${
          tone === "rose" ? "text-rose-600" : "text-slate-900"
        } ${tone === "rose" && pulse ? "at-risk-bump" : ""}`}
      >
        {value}
      </div>
      <div className={`text-[11.5px] font-semibold mt-0.5 ${toneMap[tone]}`}>
        {tone === "rose" && pulse
          ? "needs action"
          : tone === "violet"
            ? "+5% WoW"
            : "+12% WoW"}
      </div>
    </div>
  );
}
